import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { StickyNote, Plus, Edit2, Trash2, X, Search, Share2, Loader2 } from "lucide-react";
import { useDrafts } from "../../contexts/DraftsContext";
import { confirmDialog } from "../../utils/confirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("en-GB", { hour12: false }); } catch { return iso; } };

const NoteForm = ({ note, onClose, onSaved }) => {
  const isEdit = !!note?.id;
  const { requestClose, saveDraft, loadDraft, clearDirty } = useDrafts();
  const stash = useRef(loadDraft("hr-notes-form") || null).current;
  const [title, setTitle] = useState(note?.title || stash?.title || "");
  const [content, setContent] = useState(note?.content || stash?.content || "");
  const [saving, setSaving] = useState(false);

  const hasContent = !isEdit && (title.trim() || content.trim());
  useEffect(() => {
    if (!isEdit && hasContent) saveDraft("hr-notes-form", "كتابة ملاحظة جديدة", { title, content });
  }, [title, content, hasContent, isEdit, saveDraft]);

  const guardedClose = () => requestClose(hasContent, isEdit ? "تعديل ملاحظة" : "ملاحظة جديدة", onClose);

  const submit = async () => {
    if (!title.trim()) { toast.error("اكتب عنوان الملاحظة"); return; }
    setSaving(true);
    try {
      const payload = { title: title.trim(), content: content.trim() };
      if (isEdit) await axios.patch(`${API}/admin/hr-notes/${note.id}`, payload);
      else await axios.post(`${API}/admin/hr-notes`, payload);
      toast.success(isEdit ? "تم التحديث" : "تم حفظ الملاحظة");
      clearDirty("hr-notes-form");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "فشل الحفظ"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[150] bg-black/60 flex items-center justify-center p-3" dir="rtl" data-testid="hr-note-form" onClick={guardedClose}>
      <div className="bg-white rounded-3xl w-full max-w-lg max-h-[92vh] overflow-hidden shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-l from-[#F59E0B] to-[#F97316] text-white p-4 flex justify-between items-center">
          <h3 className="font-bold text-lg flex items-center gap-2"><StickyNote className="w-5 h-5" /> {isEdit ? "تعديل ملاحظة" : "ملاحظة جديدة"}</h3>
          <button onClick={guardedClose} data-testid="hr-note-close" className="bg-white/20 hover:bg-white/30 rounded-full p-1.5"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          <div>
            <label className="text-xs font-bold text-[#5C6E64] block mb-1">عنوان الملاحظة *</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="hr-note-title"
              placeholder="مثال: تذكير اجتماع غداً"
              className="w-full px-3 py-2.5 rounded-xl border-2 border-[#F59E0B] focus:outline-none font-bold" />
          </div>
          <div>
            <label className="text-xs font-bold text-[#5C6E64] block mb-1">نص الملاحظة</label>
            <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={8} data-testid="hr-note-content"
              placeholder="اكتب تفاصيل الملاحظة هنا..."
              className="w-full px-3 py-2.5 rounded-xl border-2 border-[#E4EBE6] focus:outline-none focus:border-[#F59E0B] text-sm leading-relaxed" />
          </div>
        </div>
        <div className="p-3 border-t-2 border-[#E4EBE6] flex justify-end gap-2">
          <button onClick={guardedClose} className="bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] font-bold py-2 px-4 rounded-full">إلغاء</button>
          <button onClick={submit} disabled={saving} data-testid="hr-note-submit"
            className="bg-[#15803d] hover:bg-[#126b32] disabled:bg-[#BAE6FD] text-white font-bold py-2 px-5 rounded-full flex items-center gap-1">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <StickyNote className="w-4 h-4" />}
            {isEdit ? "حفظ" : "إضافة"}
          </button>
        </div>
      </div>
    </div>
  );
};

const HRNotesTab = () => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/admin/hr-notes`); setNotes(data || []); }
    catch { toast.error("فشل التحميل"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Auto-open form when bell-dropdown resume event fires for this tab.
  useEffect(() => {
    const onResume = (e) => {
      if (e.detail?.draftId === "hr-notes-form" && !editing) setEditing("new");
    };
    window.addEventListener("hr-resume-draft", onResume);
    return () => window.removeEventListener("hr-resume-draft", onResume);
  }, [editing]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return notes;
    return notes.filter((n) => (n.title || "").toLowerCase().includes(t) || (n.content || "").toLowerCase().includes(t));
  }, [notes, q]);

  const remove = async (n) => {
    if (!(await confirmDialog(`حذف ملاحظة "${n.title}"؟`))) return;
    try { await axios.delete(`${API}/admin/hr-notes/${n.id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "فشل"); }
  };

  const share = (n) => {
    const text = encodeURIComponent(`📝 ${n.title}\n${"─".repeat(20)}\n${n.content || "(لا يوجد محتوى)"}\n\nجملتي · jumlaty.online`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  return (
    <div className="space-y-3" data-testid="hr-notes-tab" dir="rtl">
      <div className="bg-gradient-to-l from-[#F59E0B] to-[#F97316] text-white rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-1"><StickyNote className="w-5 h-5" /><h3 className="font-bold text-lg">الملاحظات</h3></div>
        <p className="text-xs opacity-90">ملاحظاتك الشخصية مع البحث + المشاركة بالواتساب.</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث عن عنوان الملاحظة..." data-testid="hr-notes-search"
            className="w-full pr-10 pl-4 py-2.5 rounded-full border-2 border-[#E4EBE6] bg-white focus:outline-none focus:border-[#F59E0B]" />
        </div>
        <button onClick={() => setEditing("new")} data-testid="hr-notes-add"
          className="bg-[#15803d] hover:bg-[#126b32] text-white font-bold py-2.5 px-4 rounded-full flex items-center gap-1">
          <Plus className="w-4 h-4" /> ملاحظة جديدة
        </button>
      </div>

      {loading ? (
        <div className="text-center py-10"><Loader2 className="w-8 h-8 animate-spin inline text-[#F59E0B]" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-[#5C6E64] py-12 bg-white rounded-2xl border-2 border-dashed border-[#E4EBE6]">
          <StickyNote className="w-10 h-10 mx-auto mb-2 opacity-60" />
          <p className="text-sm">لا توجد ملاحظات. أضف أول ملاحظة.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((n, idx) => (
            <div key={n.id} data-testid={`hr-note-${n.id}`} className="bg-white rounded-2xl border-2 border-[#F59E0B]/40 overflow-hidden shadow-sm">
              {/* Title bar */}
              <div className="bg-gradient-to-l from-[#F59E0B] to-[#F97316] text-white px-3 py-2 flex items-center justify-between">
                <p className="font-bold text-sm truncate flex items-center gap-2">
                  <span className="bg-white/25 rounded-full px-1.5 text-[10px]" dir="ltr">#{idx + 1}</span>
                  {n.title}
                </p>
                <span className="text-[10px] opacity-90" dir="ltr">{fmtDate(n.created_at).split(",")[0]}</span>
              </div>
              {/* Content */}
              <div className="p-3">
                <p className="text-sm text-[#111814] whitespace-pre-wrap leading-relaxed min-h-[40px]">{n.content || <span className="text-[#5C6E64] italic">(بدون محتوى)</span>}</p>
              </div>
              <div className="border-t border-[#FEF3C7] p-1.5 flex justify-end gap-1">
                <button onClick={() => share(n)} data-testid={`hr-note-share-${n.id}`}
                  className="bg-[#25D366] hover:bg-[#1ebe57] text-white text-[11px] font-bold py-1 px-2.5 rounded-full flex items-center gap-1">
                  <Share2 className="w-3 h-3" /> واتساب
                </button>
                <button onClick={() => setEditing(n)} data-testid={`hr-note-edit-${n.id}`}
                  className="bg-[#3B82F6] hover:bg-[#2563eb] text-white text-[11px] font-bold py-1 px-2.5 rounded-full flex items-center gap-1">
                  <Edit2 className="w-3 h-3" /> تعديل
                </button>
                <button onClick={() => remove(n)} data-testid={`hr-note-del-${n.id}`}
                  className="bg-[#FEE2E2] hover:bg-[#FECACA] text-[#E85D45] text-[11px] font-bold py-1 px-2 rounded-full flex items-center">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <NoteForm note={editing === "new" ? null : editing}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
};

export default HRNotesTab;
